/*
 * QR Code generator (byte mode), adapted from Nayuki's QR Code generator library.
 * Copyright (c) Project Nayuki. (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 *
 * Trimmed to byte-mode encoding with automatic version + mask selection, which is
 * all we need to encode share URLs. Produces a module matrix and an SVG string.
 */

// error correction level
enum Ecc {
    LOW = 1,
    MEDIUM = 0,
    QUARTILE = 3,
    HIGH = 2
}

const eccFormatBits = (e: Ecc): number => e;

// Canonical Nayuki tables, indexed by ecl ordinal {LOW,MEDIUM,QUARTILE,HIGH}={1,0,3,2}
// then by version (1..40; index 0 unused).
// ECC codewords per block:
const ECC_CW: { [k: number]: number[] } = {
    1: [-1, 7, 10, 13, 17, 22, 28, 32, 38, 42, 50, 54, 58, 64, 70, 76, 84, 90, 96, 102, 110, 116, 124, 132, 140, 148, 154, 162, 170, 180, 190, 200, 210, 220, 230, 244, 256, 270, 282, 296, 310], // LOW
    0: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // MEDIUM
    3: [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // QUARTILE
    2: [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30]  // HIGH
};

const ECC_BLOCKS: { [k: number]: number[] } = {
    1: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25], // LOW
    0: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49], // MEDIUM
    3: [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68], // QUARTILE
    2: [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81]  // HIGH
};

// Reed-Solomon ECC generator over GF(256) with primitive modulus 0x11D
const reedSolomonComputeDivisor = (degree: number): Uint8Array => {
    const result = new Uint8Array(degree);
    result[degree - 1] = 1;
    let root = 1;
    for (let i = 0; i < degree; i++) {
        for (let j = 0; j < result.length; j++) {
            result[j] = reedSolomonMultiply(result[j], root);
            if (j + 1 < result.length) {
                result[j] ^= result[j + 1];
            }
        }
        root = reedSolomonMultiply(root, 0x02);
    }
    return result;
};

const reedSolomonComputeRemainder = (data: Uint8Array, divisor: Uint8Array): Uint8Array => {
    const result = new Uint8Array(divisor.length);
    for (const b of data) {
        const factor = b ^ result[0];
        result.copyWithin(0, 1);
        result[result.length - 1] = 0;
        for (let i = 0; i < result.length; i++) {
            result[i] ^= reedSolomonMultiply(divisor[i], factor);
        }
    }
    return result;
};

const reedSolomonMultiply = (x: number, y: number): number => {
    let z = 0;
    for (let i = 7; i >= 0; i--) {
        z = (z << 1) ^ ((z >>> 7) * 0x11D);
        z ^= ((y >>> i) & 1) * x;
    }
    return z & 0xFF;
};

const getNumRawDataModules = (ver: number): number => {
    let result = (16 * ver + 128) * ver + 64;
    if (ver >= 2) {
        const numAlign = Math.floor(ver / 7) + 2;
        result -= (25 * numAlign - 10) * numAlign - 55;
        if (ver >= 7) {
            result -= 36;
        }
    }
    return result;
};

const getNumDataCodewords = (ver: number, ecl: Ecc): number => {
    return Math.floor(getNumRawDataModules(ver) / 8) -
        ECC_CW[ecl][ver] * ECC_BLOCKS[ecl][ver];
};

class QrCode {
    readonly size: number;
    private readonly modules: boolean[][] = [];
    private readonly isFunction: boolean[][] = [];

    constructor(
        readonly version: number,
        readonly ecl: Ecc,
        dataCodewords: Uint8Array,
        msk: number
    ) {
        this.size = version * 4 + 17;
        const row: boolean[] = [];
        for (let i = 0; i < this.size; i++) {
            row.push(false);
        }
        for (let i = 0; i < this.size; i++) {
            this.modules.push(row.slice());
            this.isFunction.push(row.slice());
        }

        this.drawFunctionPatterns();
        const allCodewords = this.addEccAndInterleave(dataCodewords);
        this.drawCodewords(allCodewords);

        if (msk === -1) {
            let minPenalty = 1e9;
            for (let i = 0; i < 8; i++) {
                this.applyMask(i);
                this.drawFormatBits(i);
                const penalty = this.getPenaltyScore();
                if (penalty < minPenalty) {
                    msk = i;
                    minPenalty = penalty;
                }
                this.applyMask(i); // undo
            }
        }
        this.applyMask(msk);
        this.drawFormatBits(msk);
    }

    getModule(x: number, y: number): boolean {
        return x >= 0 && x < this.size && y >= 0 && y < this.size && this.modules[y][x];
    }

    private drawFunctionPatterns(): void {
        for (let i = 0; i < this.size; i++) {
            this.setFunctionModule(6, i, i % 2 === 0);
            this.setFunctionModule(i, 6, i % 2 === 0);
        }
        this.drawFinderPattern(3, 3);
        this.drawFinderPattern(this.size - 4, 3);
        this.drawFinderPattern(3, this.size - 4);

        const alignPatPos = this.getAlignmentPatternPositions();
        const numAlign = alignPatPos.length;
        for (let i = 0; i < numAlign; i++) {
            for (let j = 0; j < numAlign; j++) {
                if (!(i === 0 && j === 0 || i === 0 && j === numAlign - 1 || i === numAlign - 1 && j === 0)) {
                    this.drawAlignmentPattern(alignPatPos[i], alignPatPos[j]);
                }
            }
        }

        this.drawFormatBits(0);
        this.drawVersion();
    }

    private drawFormatBits(mask: number): void {
        const data = eccFormatBits(this.ecl) << 3 | mask;
        let rem = data;
        for (let i = 0; i < 10; i++) {
            rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
        }
        const bits = (data << 10 | rem) ^ 0x5412;

        for (let i = 0; i <= 5; i++) {
            this.setFunctionModule(8, i, getBit(bits, i));
        }
        this.setFunctionModule(8, 7, getBit(bits, 6));
        this.setFunctionModule(8, 8, getBit(bits, 7));
        this.setFunctionModule(7, 8, getBit(bits, 8));
        for (let i = 9; i < 15; i++) {
            this.setFunctionModule(14 - i, 8, getBit(bits, i));
        }

        for (let i = 0; i < 8; i++) {
            this.setFunctionModule(this.size - 1 - i, 8, getBit(bits, i));
        }
        for (let i = 8; i < 15; i++) {
            this.setFunctionModule(8, this.size - 15 + i, getBit(bits, i));
        }
        this.setFunctionModule(8, this.size - 8, true);
    }

    private drawVersion(): void {
        if (this.version < 7) {
            return;
        }
        let rem = this.version;
        for (let i = 0; i < 12; i++) {
            rem = (rem << 1) ^ ((rem >>> 11) * 0x1F25);
        }
        const bits = this.version << 12 | rem;

        for (let i = 0; i < 18; i++) {
            const color = getBit(bits, i);
            const a = this.size - 11 + i % 3;
            const b = Math.floor(i / 3);
            this.setFunctionModule(a, b, color);
            this.setFunctionModule(b, a, color);
        }
    }

    private drawFinderPattern(x: number, y: number): void {
        for (let dy = -4; dy <= 4; dy++) {
            for (let dx = -4; dx <= 4; dx++) {
                const dist = Math.max(Math.abs(dx), Math.abs(dy));
                const xx = x + dx;
                const yy = y + dy;
                if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) {
                    this.setFunctionModule(xx, yy, dist !== 2 && dist !== 4);
                }
            }
        }
    }

    private drawAlignmentPattern(x: number, y: number): void {
        for (let dy = -2; dy <= 2; dy++) {
            for (let dx = -2; dx <= 2; dx++) {
                this.setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
            }
        }
    }

    private setFunctionModule(x: number, y: number, isDark: boolean): void {
        this.modules[y][x] = isDark;
        this.isFunction[y][x] = true;
    }

    private addEccAndInterleave(data: Uint8Array): Uint8Array {
        const ver = this.version;
        const ecl = this.ecl;
        const numBlocks = ECC_BLOCKS[ecl][ver];
        const blockEccLen = ECC_CW[ecl][ver];
        const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
        const numShortBlocks = numBlocks - rawCodewords % numBlocks;
        const shortBlockLen = Math.floor(rawCodewords / numBlocks);

        // split data into blocks and append ECC to each; short blocks get a
        // trailing padding 0 so every block has the same length for interleaving
        const blocks: number[][] = [];
        const rsDiv = reedSolomonComputeDivisor(blockEccLen);
        for (let i = 0, k = 0; i < numBlocks; i++) {
            const datLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
            const dat = Array.from(data.slice(k, k + datLen));
            k += datLen;
            const ecc = reedSolomonComputeRemainder(Uint8Array.from(dat), rsDiv);
            if (i < numShortBlocks) {
                dat.push(0);
            }
            blocks.push(dat.concat(Array.from(ecc)));
        }

        // interleave the codewords from every block
        const result: number[] = [];
        for (let i = 0; i < blocks[0].length; i++) {
            for (let j = 0; j < blocks.length; j++) {
                // skip the padding 0 cell of short blocks at the data boundary
                if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
                    result.push(blocks[j][i]);
                }
            }
        }
        return Uint8Array.from(result);
    }

    private drawCodewords(data: Uint8Array): void {
        let i = 0;
        for (let right = this.size - 1; right >= 1; right -= 2) {
            if (right === 6) {
                right = 5;
            }
            for (let vert = 0; vert < this.size; vert++) {
                for (let j = 0; j < 2; j++) {
                    const x = right - j;
                    const upward = ((right + 1) & 2) === 0;
                    const y = upward ? this.size - 1 - vert : vert;
                    if (!this.isFunction[y][x] && i < data.length * 8) {
                        this.modules[y][x] = getBit(data[i >>> 3], 7 - (i & 7));
                        i++;
                    }
                }
            }
        }
    }

    private applyMask(mask: number): void {
        for (let y = 0; y < this.size; y++) {
            for (let x = 0; x < this.size; x++) {
                let invert: boolean;
                switch (mask) {
                    case 0: invert = (x + y) % 2 === 0; break;
                    case 1: invert = y % 2 === 0; break;
                    case 2: invert = x % 3 === 0; break;
                    case 3: invert = (x + y) % 3 === 0; break;
                    case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
                    case 5: invert = x * y % 2 + x * y % 3 === 0; break;
                    case 6: invert = (x * y % 2 + x * y % 3) % 2 === 0; break;
                    case 7: invert = ((x + y) % 2 + x * y % 3) % 2 === 0; break;
                    default: invert = false;
                }
                if (!this.isFunction[y][x] && invert) {
                    this.modules[y][x] = !this.modules[y][x];
                }
            }
        }
    }

    private getPenaltyScore(): number {
        let result = 0;
        const size = this.size;
        const mods = this.modules;

        // adjacent modules in row/column with same color
        for (let y = 0; y < size; y++) {
            let runColor = false;
            let runX = 0;
            for (let x = 0; x < size; x++) {
                if (mods[y][x] === runColor) {
                    runX++;
                    if (runX === 5) {
                        result += 3;
                    } else if (runX > 5) {
                        result++;
                    }
                } else {
                    runColor = mods[y][x];
                    runX = 1;
                }
            }
        }
        for (let x = 0; x < size; x++) {
            let runColor = false;
            let runY = 0;
            for (let y = 0; y < size; y++) {
                if (mods[y][x] === runColor) {
                    runY++;
                    if (runY === 5) {
                        result += 3;
                    } else if (runY > 5) {
                        result++;
                    }
                } else {
                    runColor = mods[y][x];
                    runY = 1;
                }
            }
        }

        // 2x2 blocks of same color
        for (let y = 0; y < size - 1; y++) {
            for (let x = 0; x < size - 1; x++) {
                const color = mods[y][x];
                if (color === mods[y][x + 1] && color === mods[y + 1][x] && color === mods[y + 1][x + 1]) {
                    result += 3;
                }
            }
        }

        // balance of dark/light
        let dark = 0;
        for (const rowArr of mods) {
            for (const c of rowArr) {
                if (c) {
                    dark++;
                }
            }
        }
        const total = size * size;
        const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
        result += k * 10;
        return result;
    }

    private getAlignmentPatternPositions(): number[] {
        if (this.version === 1) {
            return [];
        }
        const numAlign = Math.floor(this.version / 7) + 2;
        const step = Math.floor((this.version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
        const result: number[] = [6];
        for (let pos = this.size - 7; result.length < numAlign; pos -= step) {
            result.splice(1, 0, pos);
        }
        return result;
    }
}

const getBit = (x: number, i: number): boolean => ((x >>> i) & 1) !== 0;

// encode text as a byte-mode QR and return the module matrix
const encodeQr = (text: string, ecl: Ecc = Ecc.MEDIUM): QrCode => {
    const utf8 = new TextEncoder().encode(text);

    // pick the smallest version that fits
    let version = 1;
    let dataCapacityBits = 0;
    for (; version <= 40; version++) {
        dataCapacityBits = getNumDataCodewords(version, ecl) * 8;
        const ccBits = version < 10 ? 8 : 16;
        const usedBits = 4 + ccBits + utf8.length * 8;
        if (usedBits <= dataCapacityBits) {
            break;
        }
        if (version === 40) {
            throw new Error('data too long for QR code');
        }
    }

    const ccBits = version < 10 ? 8 : 16;
    const bb: number[] = [];
    const appendBits = (val: number, len: number) => {
        for (let i = len - 1; i >= 0; i--) {
            bb.push((val >>> i) & 1);
        }
    };

    appendBits(0x4, 4); // byte mode indicator
    appendBits(utf8.length, ccBits);
    for (const b of utf8) {
        appendBits(b, 8);
    }

    // terminator + bit/byte padding
    appendBits(0, Math.min(4, dataCapacityBits - bb.length));
    appendBits(0, (8 - bb.length % 8) % 8);
    for (let pad = 0xEC; bb.length < dataCapacityBits; pad ^= 0xEC ^ 0x11) {
        appendBits(pad, 8);
    }

    const dataCodewords = new Uint8Array(bb.length / 8);
    for (let i = 0; i < bb.length; i++) {
        dataCodewords[i >>> 3] |= bb[i] << (7 - (i & 7));
    }

    return new QrCode(version, ecl, dataCodewords, -1);
};

/**
 * Generate an SVG string for a QR code encoding `text`.
 * `dark`/`light` are CSS colors; `border` is the quiet-zone width in modules.
 */
const qrSvg = (text: string, opts?: { dark?: string; light?: string; border?: number; size?: number }): string => {
    const dark = opts?.dark ?? '#000000';
    const light = opts?.light ?? '#ffffff';
    const border = opts?.border ?? 4;
    const qr = encodeQr(text);
    const dim = qr.size + border * 2;

    const parts: string[] = [];
    for (let y = 0; y < qr.size; y++) {
        for (let x = 0; x < qr.size; x++) {
            if (qr.getModule(x, y)) {
                parts.push(`M${x + border},${y + border}h1v1h-1z`);
            }
        }
    }

    const sizeAttr = opts?.size ? ` width="${opts.size}" height="${opts.size}"` : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}"${sizeAttr} shape-rendering="crispEdges">` +
        `<rect width="${dim}" height="${dim}" fill="${light}"/>` +
        `<path d="${parts.join('')}" fill="${dark}"/>` +
        '</svg>';
};

export { qrSvg, encodeQr, QrCode, Ecc };
